import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ApolloClient, ApolloLink, FetchResult, InMemoryCache, gql, NormalizedCacheObject } from '@apollo/client/core';
import { createHttpLink } from '@apollo/client/link/http';
import { setContext } from '@apollo/client/link/context';
import { AUTH_TYPE } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import { SessionStorageService } from 'library-explorer';
import { environment } from '@env/environment';

export interface AppSyncChatMessage {
  messageId: string;
  channelUrl: string;
  senderId: string;
  content: string;
  createdAt: string;
}

export interface SendMessageInput {
  channelUrl: string;
  content: string;
  tenantId?: string;
}

export interface MessagesPage {
  items: AppSyncChatMessage[];
  nextToken?: string;
}

export interface GetMessagesOptions {
  limit?: number;
  nextToken?: string;
}

const GET_MESSAGES_QUERY = gql`
  query GetMessages($channelUrl: ID!, $tenantId: String, $limit: Int, $nextToken: String) {
    getMessages(channelUrl: $channelUrl, tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        messageId
        senderId
        content
        createdAt
      }
      nextToken
    }
  }
`;

const SEND_MESSAGE_MUTATION = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      messageId
      channelUrl
      senderId
      content
      createdAt
    }
  }
`;

const ON_NEW_MESSAGE_QUERY = gql`
  subscription OnNewMessage($channelUrl: ID!) {
    onNewMessage(channelUrl: $channelUrl) {
      messageId
      channelUrl
      senderId
      content
      createdAt
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private readonly apolloClient: ApolloClient<NormalizedCacheObject>;

  constructor(
    private readonly sessionStorageService: SessionStorageService
  ) {
    this.apolloClient = this.createApolloClient();
  }

  public getMessages(channelUrl: string, options: GetMessagesOptions = {}): Observable<MessagesPage> {
    return from(
      this.apolloClient.query<{ getMessages: MessagesPage }>({
        query: GET_MESSAGES_QUERY,
        variables: {
          channelUrl,
          tenantId: 'internal',
          limit: options.limit ?? null,
          nextToken: options.nextToken ?? null,
        },
      })
    ).pipe(map(res => ({
      items: res.data?.getMessages?.items ?? [],
      nextToken: res.data?.getMessages?.nextToken ?? undefined,
    })));
  }

  public sendMessage(input: SendMessageInput): Observable<AppSyncChatMessage> {
    const payload = { ...input, tenantId: 'internal' };
    return from(
      this.apolloClient.mutate<{ sendMessage: AppSyncChatMessage }>({
        mutation: SEND_MESSAGE_MUTATION,
        variables: { input: payload },
      })
    ).pipe(map(res => res.data!.sendMessage));
  }

  public subscribeToNewMessages(channelUrl: string): Observable<AppSyncChatMessage> {
    return new Observable<FetchResult<{ onNewMessage: AppSyncChatMessage }>>(observer => {
      const subscription = this.apolloClient.subscribe<{ onNewMessage: AppSyncChatMessage }>({
        query: ON_NEW_MESSAGE_QUERY,
        variables: { channelUrl },
      }).subscribe({
        next: result => observer.next(result),
        error: error => observer.error(error),
        complete: () => observer.complete(),
      });

      return () => subscription.unsubscribe();
    }).pipe(
      map(res => res.data?.onNewMessage),
      filter((msg): msg is AppSyncChatMessage => !!msg),
    );
  }

  private buildAuthToken(): string {
    return `${'internal'}:${this.sessionStorageService.getJwtTokenValue()}`;
  }

  private createApolloClient(): ApolloClient<NormalizedCacheObject> {
    const url = environment.chatGraphqlUrl;
    const auth = {
      type: AUTH_TYPE.AWS_LAMBDA as 'AWS_LAMBDA',
      token: () => this.buildAuthToken(),
    };

    const httpLink = createHttpLink({ uri: url });
    const authLink = setContext((_, { headers }) => ({
      headers: { ...headers, Authorization: this.buildAuthToken() },
    }));

    const link = ApolloLink.from([
      createSubscriptionHandshakeLink(
        { url, region: 'eu-west-1', auth },
        authLink.concat(httpLink)
      ),
    ]);

    return new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        query: { fetchPolicy: 'no-cache' },
        mutate: { fetchPolicy: 'no-cache' },
      },
    });
  }
}
